// Library Methods
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Types
import { ColorVisionDeficiencyType } from "src/constants/enums";
import { IshiharaStateResponseType } from "src/services/exams/types";

type IshiharaResultType = {
  colorVisionResult: IshiharaStateResponseType;
  resultIndex: number;
  isLiveUpdate?: boolean;
};

const IshiharaResult = ({
  colorVisionResult,
  resultIndex,
  isLiveUpdate = false,
}: IshiharaResultType) => {
  const { t } = useTranslation();

  const [isImageError, setIsImageError] = useState(false);

  // Smaller plate image and response/expected-response text in the
  // height-constrained live update panel.
  const imageSize = isLiveUpdate ? "80px" : "130px";
  const textFontSize = isLiveUpdate ? "12px" : "14px";

  const getIsCorrectResponse = useCallback(
    (colorVisionResult: IshiharaStateResponseType) => {
      if (colorVisionResult?.response) {
        return (
          colorVisionResult?.response?.responseData ===
          colorVisionResult?.response?.expectedResponses?.NormalVision
        );
      }

      return (
        colorVisionResult?.responseData ===
        colorVisionResult?.plate?.expectedResponses?.NormalVision
      );
    },
    []
  );

  return (
    <Grid
      key={resultIndex}
      display="flex"
      alignItems="flex-start"
      sx={{
        pageBreakAfter:
          resultIndex === 5 || resultIndex === 13 || resultIndex === 21
            ? "always"
            : "avoid",
      }}
    >
      <Grid display="flex" flexDirection="column" alignItems="center" gap="8px">
        <Typography
          fontSize={textFontSize}
          fontWeight={600}
          sx={{ textTransform: "capitalize" }}
        >
          {t("Plate")} {colorVisionResult?.plate?.plateType?.split("_")?.[1]}
        </Typography>
        {isImageError ? (
          <Box
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              height: imageSize,
              width: imageSize,
              maxHeight: imageSize,
              maxWidth: imageSize,
            }}
          >
            <Typography
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                fontSize: textFontSize,
                borderRadius: "50%",
                backgroundColor: "#5B6D7A40",
              }}
            >
              {t("Unable to load image.")}
            </Typography>
          </Box>
        ) : (
          <img
            alt="color vision results ishihara plate"
            style={{
              borderRadius: "50%",
              border: "1px solid black",
              height: imageSize,
              width: imageSize,
              maxHeight: imageSize,
              maxWidth: imageSize,
            }}
            src={`data:image/png;base64,${colorVisionResult?.plate?.plateImage}`}
            onError={() => setIsImageError(true)}
          />
        )}
      </Grid>

      <Grid display="flex" flexDirection="column" gap="2px" px="8px">
        <Typography
          mb="6px"
          visibility={colorVisionResult?.responseData ? "visible" : "hidden"}
          fontSize={textFontSize}
          fontWeight={600}
          color={
            getIsCorrectResponse(colorVisionResult) ? "#13A321" : "#B21515"
          }
        >
          {`${t("Response")}: ${t(colorVisionResult?.responseData ?? "")}`}
        </Typography>

        <Typography fontSize={textFontSize} fontWeight={600}>
          {t("Expected Responses")}:
        </Typography>

        {Object.keys(colorVisionResult?.plate?.expectedResponses ?? {})?.map(
          (key) => (
            <Typography
              key={`expected-response-${colorVisionResult?.plate?.plateType}-${key}`}
              fontSize={textFontSize}
            >
              {t(key)}:{" "}
              {t(
                colorVisionResult?.plate?.expectedResponses?.[
                  key as ColorVisionDeficiencyType
                ] ?? ""
              )}
            </Typography>
          )
        )}
      </Grid>
    </Grid>
  );
};

export default IshiharaResult;
